/**
 * Outbound effect handlers — extracted from MultiplayerManager.onPostUpdate().
 *
 * Mirrors handlers-gameplay.ts (inbound) and handlers-connection.ts (lifecycle).
 * Processes game effects each frame and sends the corresponding network messages.
 *
 * Handles: fire_action, di_resolve, burst_resolve, special_trigger, edgeguard_fire,
 * tank_select, stage_select, firing_resolve, death_trigger, defense_resolve,
 * state_hash, turn_snapshot, wait-for-authority flags, mp_waiting reset
 */

import { hashGameState } from './sync';
import { resetGame } from '../game/reset';
import { applyEffects, type Renderers } from '../game/effects';
import type { GameState } from '../types';
import type { GameEffect } from '../game/effects';
import type { GameClient } from './client';
import type { NetworkInput } from '../input';
import type { NetDebugLog } from './debug-log';

export interface OutboundDeps {
    readonly client: GameClient;
    readonly debugLog: NetDebugLog | null;
    readonly networkInput: NetworkInput;
    readonly recoveryInput: NetworkInput;
    getState(): GameState;
    getRenderers(): Renderers | null;
    isMyTurn(): boolean;
    getPreFireSnapshot(): { x: number; y: number; angle: number; power: number };
    getPendingRoomSeed(): number;
    setPendingRoomSeed(seed: number): void;
    setMpWaitUntilFrame(frame: number): void;
    setLastGrapplePhase(phase: string | null): void;
    getPendingCheckpointId(): number;
    setPendingCheckpointId(id: number): void;
    /** Apply a buffered turn_snapshot (arrived before advanceTurn) and send deferred hash. */
    applyBufferedSnapshotAndHash(): void;
}

export function processOutboundEffects(fx: GameEffect[], deps: OutboundDeps): void {
    const state = deps.getState();
    if (!state.multiplayer?.enabled) return;

    const isMyTurn = deps.isMyTurn();

    for (const effect of fx) {
        if (effect.type === 'phase_transition') {
            // game_over: slot 0 (host) sends game_result to server for leaderboard
            if (effect.to === 'game_over' && state.multiplayer!.mySlot === 0 &&
                (state.modeState.mode === 'vs_online')) {
                const winnerIdx = state.winner;
                const loserIdx = winnerIdx === 0 ? 1 : 0;
                if (winnerIdx != null) {
                    const winner = state.players[winnerIdx];
                    const loser = state.players[loserIdx];
                    const ms = state.matchStats;
                    deps.client.send({
                        type: 'game_result',
                        roomCode: state.multiplayer!.roomCode,
                        timestamp: Date.now(),
                        winnerName: winner.name,
                        loserName: loser.name,
                        winnerTankId: winner.tankId,
                        loserTankId: loser.tankId,
                        stageId: state.stageId,
                        turnCount: state.turnCount,
                        winnerStats: {
                            damageDealt: ms.damageDealt[winnerIdx],
                            bestSingleHit: ms.bestSingleHit[winnerIdx],
                            voidKills: ms.voidKills[winnerIdx],
                            skyKills: ms.skyKills[winnerIdx],
                            kills: ms.kills[winnerIdx],
                            bestCombo: ms.bestCombo[winnerIdx],
                            totalTrickScore: ms.totalTrickScore[winnerIdx],
                        },
                        loserStats: {
                            damageDealt: ms.damageDealt[loserIdx],
                            bestSingleHit: ms.bestSingleHit[loserIdx],
                            voidKills: ms.voidKills[loserIdx],
                            skyKills: ms.skyKills[loserIdx],
                            kills: ms.kills[loserIdx],
                            bestCombo: ms.bestCombo[loserIdx],
                            totalTrickScore: ms.totalTrickScore[loserIdx],
                        },
                    });
                    if (import.meta.env.DEV) console.log(`[NET] game_result sent: ${winner.name} beat ${loser.name}`);
                }
            }

            // Clear stale networkInput on phase transitions where the key_state sender
            // changes (e.g. defender → attacker, DI player → attacker, burst player → aimer).
            // Without this, the previous sender's held keys persist until the new sender's
            // first key_state arrives, causing phantom directional input.
            const senderChanges =
                // DI/edgeguard/defense/burst entries: sender switches to a different player
                effect.to === 'di_window' ||
                effect.to === 'edgeguard' ||
                effect.to === 'defensive_movement' ||
                effect.to === 'burst_window' ||
                // Returning to aiming/awaiting from reactive phases: sender switches back
                (effect.to === 'aiming' && effect.from !== 'round_start' && effect.from !== 'lottery_pick' && effect.from !== 'lottery_spin') ||
                effect.to === 'awaiting_next_turn';
            if (senderChanges) {
                deps.networkInput.clear();
            }

            // Fire detection: aiming → firing on my turn
            if (isMyTurn && effect.from === 'aiming' && effect.to === 'firing') {
                const player = state.players[state.multiplayer.mySlot];
                // Snapshot all non-firer positions so hit detection is deterministic.
                // Without this, settlement micro-drift causes grazing shots to hit
                // on one client and miss on the other.
                const targets: Array<{ playerIndex: number; x: number; y: number }> = [];
                for (let i = 0; i < state.players.length; i++) {
                    if (i === state.multiplayer.mySlot) continue;
                    const t = state.players[i];
                    targets.push({ playerIndex: i, x: t.x, y: t.y });
                }
                const preFireSnapshot = deps.getPreFireSnapshot();
                if (import.meta.env.DEV) console.log('[NET] Fire detected! Sending fire_action, power:', preFireSnapshot.power);
                deps.client.send({
                    type: 'fire_action',
                    angle: player.angle,
                    power: preFireSnapshot.power,
                    x: player.x,
                    y: player.y,
                    vx: player.vx,
                    vy: player.vy,
                    weaponId: player.selectedWeapon?.id ?? 'mortar',
                    targets,
                });
            }

            // DI window entry: if I'm NOT the DI player, wait for authority
            if (effect.to === 'di_window') {
                if (state.diActivePlayer !== state.multiplayer.mySlot) {
                    state.diWaitForAuthority = true;
                    if (import.meta.env.DEV) console.log(`[NET] DI window opened for P${state.diActivePlayer} (not me) — waiting for authority`);
                }
            }

            // Burst window entry: if I'm NOT the burst player, wait for authority
            // Use burstActivePlayer (not currentPlayer) — defensive burst is for the defender, not the turn owner
            if (effect.to === 'burst_window') {
                if (state.burstActivePlayer !== state.multiplayer.mySlot) {
                    state.burstWaitForAuthority = true;
                    if (import.meta.env.DEV) console.log(`[NET] Burst window opened for P${state.burstActivePlayer} (not me) — waiting for authority`);
                }
            }

            // Defensive movement entry: if I'm NOT the defender, wait for authority
            if (effect.to === 'defensive_movement') {
                if (state.defenseActivePlayer >= 0 &&
                    state.defenseActivePlayer !== state.multiplayer!.mySlot) {
                    state.defenseWaitForAuthority = true;
                    if (import.meta.env.DEV) console.log(`[NET] Defense phase — P${state.defenseActivePlayer} defending (not me) — waiting for authority`);
                }
            }
        }

        if (effect.type === 'turn_boundary_checkpoint') {
            // Reset grapple tracking to prevent spurious cancel on next turn
            deps.setLastGrapplePhase(null);
            // Clear network + recovery input to prevent stale keys carrying into next turn.
            // Without this, the previous sender's held keys (e.g. ArrowLeft from defense)
            // persist in networkInput until the new sender's first key_state arrives,
            // causing phantom input for 1-3+ frames at the start of each turn.
            deps.networkInput.clear();
            deps.recoveryInput.clear();

            // Capture hash checkpoint for desync diffing (DEV debugger)
            deps.debugLog?.captureHashCheckpoint(state);

            // Increment logical checkpoint counter — both clients increment
            // identically at each turn boundary (unlike frameCount which drifts
            // due to async game start timing).
            state.hashCheckpointId++;

            // Send authoritative turn snapshot so both clients agree
            // before the next turn starts. Only the player whose turn
            // just ended sends this (advanceTurn already ran, so previous
            // player = 1 - currentPlayer in a 2-player game).
            const prevPlayer = 1 - state.currentPlayer;
            if (prevPlayer === state.multiplayer.mySlot) {
                // I'm the turn owner — compute hash immediately and send snapshot
                const hash = hashGameState(state);
                deps.client.send({
                    type: 'state_hash',
                    checkpointId: state.hashCheckpointId,
                    hash,
                });

                const snapshot = state.players.map(p => ({
                    x: p.x, y: p.y,
                    vx: p.vx, vy: p.vy,
                    angle: p.angle, power: p.power,
                    alive: p.alive,
                    damagePercent: p.damagePercent,
                    momentum: p.momentum,
                    weaponCharge: p.weaponCharge,
                    tumble: p.tumble,
                    settled: p.settled,
                    liftFuel: p.liftFuel,
                    liftActive: p.liftActive,
                    fastFallActive: p.fastFallActive,
                    armedShotTier: p.armedShotTier,
                    collapseGrabbed: p.collapseGrabbed,
                    collapseFrame: p.collapseFrame,
                    collapseTargetX: p.collapseTargetX,
                    collapseTargetY: p.collapseTargetY,
                    portalImmunityFrames: p.portalImmunityFrames,
                    onWireframe: p.onWireframe,
                    stocks: p.stocks,
                    respawnTimer: p.respawnTimer,
                    respawnInvulnFrames: p.respawnInvulnFrames,
                    cupidMarkTurns: p.cupidMarkTurns,
                    cupidMarkedBy: p.cupidMarkedBy,
                }));
                deps.client.send({
                    type: 'turn_snapshot',
                    players: snapshot,
                    currentPlayer: state.currentPlayer,
                });
            } else {
                // Not the turn owner — defer hash until turn_snapshot arrives.
                // Walking drift from key_state network latency can cause 1-3px
                // position differences; the snapshot corrects them before hashing.
                deps.setPendingCheckpointId(state.hashCheckpointId);

                // Check if the snapshot already arrived before we reached this checkpoint
                // (race: turn owner settled faster, sent snapshot before our advanceTurn ran).
                // If buffered, apply it now (post-advanceTurn) and hash immediately.
                deps.applyBufferedSnapshotAndHash();

                if (deps.getPendingCheckpointId() >= 0) {
                    // Still waiting for snapshot (normal case)
                    if (import.meta.env.DEV) console.log(`[NET] Deferring hash until turn_snapshot (checkpoint ${state.hashCheckpointId})`);
                }
            }
        }

        if (effect.type === 'di_resolved') {
            const resolvedPlayer = effect.playerIndex;
            // Send if the resolved player is on my client (I was the DI player)
            if (resolvedPlayer === state.multiplayer.mySlot) {
                deps.client.send({
                    type: 'di_resolve',
                    playerIndex: resolvedPlayer,
                    vx: effect.vx,
                    vy: effect.vy,
                    x: effect.x,
                    y: effect.y,
                    hitstunFrames: effect.hitstunFrames,
                });
                if (import.meta.env.DEV) console.log(`[NET] DI resolve sent: P${resolvedPlayer} pos=(${effect.x.toFixed(1)}, ${effect.y.toFixed(1)}) vel=(${effect.vx.toFixed(2)}, ${effect.vy.toFixed(2)}) ragdoll=${effect.hitstunFrames}`);
            }
        }

        if (effect.type === 'burst_resolved') {
            const resolvedPlayer = effect.playerIndex;
            // Send if the resolved player is on my client (I was the burst player)
            if (resolvedPlayer === state.multiplayer.mySlot) {
                deps.client.send({
                    type: 'burst_resolve',
                    playerIndex: resolvedPlayer,
                    vx: effect.vx,
                    vy: effect.vy,
                    x: effect.x,
                    y: effect.y,
                    momentum: effect.momentum,
                    weaponCharge: effect.weaponCharge,
                    hitstunFrames: effect.hitstunFrames,
                    braced: effect.braced,
                    damagePercent: effect.damagePercent,
                });
                if (import.meta.env.DEV) console.log(`[NET] Burst resolve sent: P${resolvedPlayer} pos=(${effect.x.toFixed(1)}, ${effect.y.toFixed(1)}) vel=(${effect.vx.toFixed(2)}, ${effect.vy.toFixed(2)}) momentum=${effect.momentum} ragdoll=${effect.hitstunFrames} braced=${effect.braced}`);
            }
        }

        if (effect.type === 'special_triggered') {
            if (effect.playerIndex === state.multiplayer.mySlot) {
                deps.client.send({
                    type: 'special_trigger',
                    playerIndex: effect.playerIndex,
                });
            }
        }

        if (effect.type === 'edgeguard_fired') {
            // Send authoritative beam fire data to opponent.
            // Only the attacker (currentPlayer) fires — opponent receives
            // the message and fires with authoritative position/angle.
            // Includes target snapshots so hit detection is deterministic.
            if (isMyTurn) {
                deps.client.send({
                    type: 'edgeguard_fire',
                    angle: effect.angle,
                    x: effect.x,
                    y: effect.y,
                    targets: effect.targets,
                });
                if (import.meta.env.DEV) console.log(`[NET] Edgeguard fire sent: angle=${effect.angle.toFixed(2)} pos=(${effect.x.toFixed(1)}, ${effect.y.toFixed(1)}) targets=${effect.targets.length}`);
            }
        }

        if (effect.type === 'tank_select_confirmed') {
            // Send tank selection to opponent (include terraformerMode for Terraformer)
            if (effect.playerIndex === state.multiplayer.mySlot) {
                const player = state.players[effect.playerIndex];
                deps.client.send({
                    type: 'tank_select',
                    tankId: effect.tankId,
                    terraformerMode: player.terraformerMode,
                });
                if (import.meta.env.DEV) console.log(`[NET] Tank select sent: ${effect.tankId} (mode: ${player.terraformerMode})`);
            }
        }

        if (effect.type === 'stage_select_confirmed') {
            // Send stage selection to opponent
            deps.client.send({
                type: 'stage_select',
                stageId: effect.stageId,
                seed: effect.seed,
                themeId: effect.themeId,
            });
            // For random stages, override pendingRoomSeed with the preview seed
            // so resetGame generates the same terrain as the preview
            if (effect.seed != null) {
                deps.setPendingRoomSeed(effect.seed);
            }
            if (import.meta.env.DEV) console.log(`[NET] Stage select sent: ${effect.stageId || 'random'} (seed: ${effect.seed}, theme: ${effect.themeId})`);
        }

        // Both players confirmed stage select → resetGame with stored seed
        if (effect.type === 'phase_transition' && effect.from === 'stage_select' && effect.to === 'mp_waiting') {
            const renderers = deps.getRenderers();
            if (renderers) {
                const fx2 = resetGame(state, deps.getPendingRoomSeed());
                applyEffects(fx2, renderers);
            }
            // Set mp_waiting AFTER resetGame (which sets phase='aiming' internally)
            state.phase = 'mp_waiting';
            deps.setMpWaitUntilFrame(90);
        }

        if (effect.type === 'firing_resolved') {
            // Send firing resolve checkpoint — only the firing player (authority) sends
            if (isMyTurn) {
                deps.client.send({
                    type: 'firing_resolve',
                    players: effect.players,
                    diOpened: effect.diOpened,
                });
                if (import.meta.env.DEV) console.log(`[NET] Firing resolve sent: ${effect.players.length} knocked players, diOpened=${effect.diOpened}`);
            }
        }

        if (effect.type === 'death_triggered') {
            // Send authoritative death trigger to opponent
            deps.client.send({
                type: 'death_trigger',
                playerIndex: effect.playerIndex,
                deathType: effect.deathType,
                x: effect.x,
                y: effect.y,
            });
            if (import.meta.env.DEV) console.log(`[NET] Death trigger sent: P${effect.playerIndex} ${effect.deathType} at (${effect.x.toFixed(1)}, ${effect.y.toFixed(1)})`);
            deps.debugLog?.addEvent('snap_correction', state.frameCount, `death P${effect.playerIndex} ${effect.deathType}`, 'warn');
        }

        if (effect.type === 'defense_resolved') {
            const resolvedPlayer = effect.playerIndex;
            // Send if the resolved player is on my client (I was the defending player)
            if (resolvedPlayer === state.multiplayer.mySlot) {
                deps.client.send({
                    type: 'defense_resolve',
                    playerIndex: resolvedPlayer,
                    vx: effect.vx,
                    vy: effect.vy,
                    x: effect.x,
                    y: effect.y,
                    tumble: effect.tumble,
                    hitstunFrames: effect.hitstunFrames,
                    tumbleRecoveryFrame: effect.tumbleRecoveryFrame,
                    tumbleRecoveryMax: effect.tumbleRecoveryMax,
                    momentum: effect.momentum,
                    weaponCharge: effect.weaponCharge,
                    settled: effect.settled,
                    grounded: effect.grounded,
                    liftFuel: effect.liftFuel,
                    liftActive: effect.liftActive,
                    fastFallActive: effect.fastFallActive,
                    landingTimer: effect.landingTimer,
                    landingTimerMax: effect.landingTimerMax,
                    defenseSubPhase: effect.defenseSubPhase,
                    onWireframe: effect.onWireframe,
                });
                if (import.meta.env.DEV) console.log(`[NET] Defense resolve sent: P${resolvedPlayer} pos=(${effect.x.toFixed(1)}, ${effect.y.toFixed(1)}) vel=(${effect.vx.toFixed(2)}, ${effect.vy.toFixed(2)}) tumble=${effect.tumble} momentum=${effect.momentum.toFixed(1)}`);
            }
        }
    }
}
